/**
 * Mass Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de massa.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - International Yard and Pound Agreement (1959)
 * - SI Brochure, 9th Edition (2019)
 *
 * Nota: Massa ≠ Peso
 * - Massa: quantidade de matéria (invariante)
 * - Peso: força gravitacional (varia com localização)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type MassUnit =
  // SI / Metric
  | "metric_ton"
  | "kilogram"
  | "hectogram"
  | "decagram"
  | "gram"
  | "decigram"
  | "centigram"
  | "milligram"
  | "microgram"
  // Avoirdupois (US/UK)
  | "long_ton"
  | "short_ton"
  | "stone"
  | "pound"
  | "ounce"
  | "dram"
  | "grain"
  // Troy (metais preciosos)
  | "troy_pound"
  | "troy_ounce"
  | "pennyweight";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de massa.
 *
 * Base: kilogram (kg) - Unidade SI de massa
 *
 * TODOS os fatores são EXATOS:
 * - SI: derivados de potências de 10
 * - Avoirdupois: exatos desde International Yard and Pound Agreement (1959)
 *   - 1 pound = 0.45359237 kg (exato por definição)
 *   - Outras derivadas de pound
 * - Troy: derivados de grain e pound avoirdupois
 */
export const MASS_UNITS: Record<MassUnit, SimpleUnitConfig> = {
  // ===========================================================================
  // SI / Metric
  // ===========================================================================
  metric_ton: {
    factor: 1000,
    symbol: "t",
    singular: "metric ton",
    plural: "metric tons",
  },
  kilogram: {
    factor: 1,
    symbol: "kg",
    singular: "kilogram",
    plural: "kilograms",
  },
  hectogram: {
    factor: 0.1,
    symbol: "hg",
    singular: "hectogram",
    plural: "hectograms",
  },
  decagram: {
    factor: 0.01,
    symbol: "dag",
    singular: "decagram",
    plural: "decagrams",
  },
  gram: {
    factor: 0.001,
    symbol: "g",
    singular: "gram",
    plural: "grams",
  },
  decigram: {
    factor: 0.0001,
    symbol: "dg",
    singular: "decigram",
    plural: "decigrams",
  },
  centigram: {
    factor: 0.00001,
    symbol: "cg",
    singular: "centigram",
    plural: "centigrams",
  },
  milligram: {
    factor: 0.000001,
    symbol: "mg",
    singular: "milligram",
    plural: "milligrams",
  },
  microgram: {
    factor: 0.000000001,
    symbol: "μg",
    singular: "microgram",
    plural: "micrograms",
  },

  // ===========================================================================
  // Avoirdupois (US/UK) - Sistema padrão para peso comum
  // ===========================================================================
  long_ton: {
    factor: 1016.0469088, // 2240 lb (exato)
    symbol: "long tn",
    singular: "long ton",
    plural: "long tons",
  },
  short_ton: {
    factor: 907.18474, // 2000 lb (exato)
    symbol: "sh tn",
    singular: "short ton",
    plural: "short tons",
  },
  stone: {
    factor: 6.35029318, // 14 lb (exato)
    symbol: "st",
    singular: "stone",
    plural: "stone", // stone não muda no plural em inglês
  },
  pound: {
    factor: 0.45359237, // Exato desde 1959
    symbol: "lb",
    singular: "pound",
    plural: "pounds",
  },
  ounce: {
    factor: 0.028349523125, // 1/16 lb (exato)
    symbol: "oz",
    singular: "ounce",
    plural: "ounces",
  },
  dram: {
    factor: 0.0017718451953125, // 1/16 oz (exato)
    symbol: "dr",
    singular: "dram",
    plural: "drams",
  },
  grain: {
    factor: 0.00006479891, // 1/7000 lb (exato)
    symbol: "gr",
    singular: "grain",
    plural: "grains",
  },

  // ===========================================================================
  // Troy (metais preciosos)
  // ===========================================================================
  troy_pound: {
    factor: 0.3732417216, // 12 troy oz (exato)
    symbol: "lb t",
    singular: "troy pound",
    plural: "troy pounds",
  },
  troy_ounce: {
    factor: 0.0311034768, // 480 grains (exato)
    symbol: "oz t",
    singular: "troy ounce",
    plural: "troy ounces",
  },
  pennyweight: {
    factor: 0.00155517384, // 1/20 troy oz (exato)
    symbol: "dwt",
    singular: "pennyweight",
    plural: "pennyweights",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const MASS_ALIASES: UnitAliases = {
  // metric_ton
  t: "metric_ton",
  // kilogram
  kg: "kilogram",
  // hectogram
  hg: "hectogram",
  // decagram
  dag: "decagram",
  // gram
  g: "gram",
  // decigram
  dg: "decigram",
  // centigram
  cg: "centigram",
  // milligram
  mg: "milligram",
  // microgram
  "μg": "microgram",
  ug: "microgram",
  mcg: "microgram",
  // long_ton
  "long tn": "long_ton",
  // short_ton
  "sh tn": "short_ton",
  // stone
  st: "stone",
  // pound
  lb: "pound",
  lbs: "pound",
  "#": "pound",
  // ounce
  oz: "ounce",
  // dram
  dr: "dram",
  // grain
  gr: "grain",
  // troy_pound
  "lb t": "troy_pound",
  // troy_ounce
  "oz t": "troy_ounce",
  ozt: "troy_ounce",
  // pennyweight
  dwt: "pennyweight",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const MASS_UNIT_LIST = Object.keys(MASS_UNITS) as MassUnit[];

/** Fatores de conversão (para compatibilidade) */
export const MASS_FACTORS: Record<MassUnit, number> = Object.fromEntries(
  Object.entries(MASS_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<MassUnit, number>;
